/**
 * Data layer for `creative-hosting-inside-campaign`. Thin typed fetch wrappers over /api/v1/creative-hosting-inside-campaign.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CreativeHostingInsideCampaignRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCreativeHostingInsideCampaignResponse {
	count: number;
	items: CreativeHostingInsideCampaignRow[];
}

export interface CreateCreativeHostingInsideCampaignBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/creative-hosting-inside-campaign";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCreativeHostingInsideCampaignResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCreativeHostingInsideCampaignResponse>(res);
}

export async function create(body: CreateCreativeHostingInsideCampaignBody): Promise<CreativeHostingInsideCampaignRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CreativeHostingInsideCampaignRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const creativeHostingInsideCampaignQueryKeys = {
	all: ["creative-hosting-inside-campaign"] as const,
	list: () => [...creativeHostingInsideCampaignQueryKeys.all] as const,
	detail: (id: string) => [...creativeHostingInsideCampaignQueryKeys.all, id] as const,
};
